import React, { useState, useEffect } from "react";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { Spin, Pagination } from "antd";
import dayjs from "dayjs";

const EnterpriseActivityLog = ({ entId, entName }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs = [], loading = false, totalRecords = 0 } = useSelector((state) => state?.activityLog) || {};

  const formatPayloadValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "object") return JSON.stringify(value);

    const strVal = String(value).trim();

    if (key === "discountRate" || key === "annualDiscountRate") {
      const cleanStr = strVal.replace(/%/g, "").trim();
      const num = parseFloat(cleanStr);
      if (!isNaN(num)) {
        const percentage = (num > 0 && num <= 1) ? num * 100 : num;
        const rounded = Number.isInteger(percentage) ? percentage : parseFloat(percentage.toFixed(2));
        return `${rounded}%`;
      }
      return strVal.endsWith("%") ? strVal : `${strVal}%`;
    }

    if (
      key === "creditExposure" ||
      key === "requestedCredit" ||
      key === "approvedCredit" ||
      key === "newCredit" ||
      key === "currentCredit" ||
      key === "creditLimit"
    ) {
      const num = Number(strVal.replace(/,/g, ""));
      if (!isNaN(num)) {
        return num.toLocaleString("en-IN");
      }
    }

    return strVal;
  };

  useEffect(() => {
    if (entId) {
      dispatch(getActivityLog({ module: "ENTERPRISE", entityId: entId, page, limit }));
    }
  }, [dispatch, entId, page]);

  const getActionConfig = (action) => {
    switch (action) {
      case "ENTERPRISE_CREATED": return { label: "Add Enterprise", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_APPROVED": return { label: "Approve Enterprise", color: "#e6ffed", textColor: "#22c55e" };
      case "ENTERPRISE_REJECTED": return { label: "Reject Enterprise", color: "#fff1f0", textColor: "#f5222d" };
      case "ENTERPRISE_PROFILE_UPDATED": return { label: "Profile Update", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_DEACTIVATED": return { label: "Deactivation", color: "#fff0f6", textColor: "#eb2f96" };
      case "ENTERPRISE_BLACKLISTED": return { label: "Blacklist", color: "#fff1f0", textColor: "#f5222d" };
      case "ENTERPRISE_REACTIVATED": return { label: "Reactivation", color: "#e6ffed", textColor: "#22c55e" };
      case "BUS_ENT_ACTIVATED": return { label: "Business Activated", color: "#e6ffed", textColor: "#22c55e" };
      case "BUS_ENT_REJECTED": return { label: "Business Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "BUS_ENT_REOPENED": return { label: "Business Reopened", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_POC_CREATED": return { label: "POC Created", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_POC_UPDATED": return { label: "POC Updated", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_POC_DELETED": return { label: "POC Deleted", color: "#fff1f0", textColor: "#f5222d" };
      case "ENTERPRISE_POC_APPROVED": return { label: "POC Approved", color: "#e6ffed", textColor: "#22c55e" };
      case "ENTERPRISE_POC_REJECTED": return { label: "POC Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "ENTERPRISE_POC_REOPENED": return { label: "POC Reopened", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_POC_MAIL_SENT": return { label: "POC Mail Sent", color: "#e6f7ff", textColor: "#1890ff" };
      default: return { label: action.replace(/_/g, " "), color: "#e6f7ff", textColor: "#1890ff" };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const targetName = entName || log.payload?.entName || "Enterprise";
    const pocName = log.payload?.name || log.payload?.pocName || "POC";
    const pocId = log.payload?.pocId ? ` (${log.payload.pocId})` : "";
    const pocDept = log.payload?.department ? ` · ${log.payload.department}` : "";
    
    if (log.action === "ENTERPRISE_CREATED") {
      return `<strong>${name}</strong> (${role}) <strong>REGISTERED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_APPROVED") {
      return `<strong>${name}</strong> (${role}) <strong>APPROVED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_REJECTED") {
      return `<strong>${name}</strong> (${role}) <strong>REJECTED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_DEACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>DEACTIVATED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_BLACKLISTED") {
      return `<strong>${name}</strong> (${role}) <strong>BLACKLISTED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_REACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>REACTIVATED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_DELETED") {
      return `<strong>${name}</strong> (${role}) <strong>DELETED</strong> POC <strong>"${pocName}"</strong>${pocId}${pocDept} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_CREATED") {
      return `<strong>${name}</strong> (${role}) <strong>CREATED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_UPDATED") {
      return `<strong>${name}</strong> (${role}) <strong>UPDATED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_APPROVED") {
      return `<strong>${name}</strong> (${role}) <strong>APPROVED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_REJECTED") {
      return `<strong>${name}</strong> (${role}) <strong>REJECTED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_REOPENED") {
      return `<strong>${name}</strong> (${role}) <strong>REOPENED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    
    const actionConfig = getActionConfig(log.action);
    return `<strong>${name}</strong> (${role}) <strong>PERFORMED ${actionConfig.label.toUpperCase()}</strong> on enterprise <strong>"${targetName}"</strong>`;
  };

  return (
    <div style={{ padding: "24px 0", maxWidth: "900px", margin: "0 auto", fontFamily: "Inter, sans-serif" }}>
      <div style={{ border: "1px solid #f0f0f0", borderRadius: "8px", overflow: "hidden", background: "#ffffff" }}>
        
        <div 
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            padding: "16px 24px", 
            background: "#fafafa", 
            borderBottom: isCollapsed ? "none" : "1px solid #f0f0f0",
            cursor: "pointer",
            userSelect: "none"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#262626" }}>Enterprise Lifecycle History</span>
            <span style={{ 
              background: "#e8e8e8", 
              color: "#595959", 
              fontSize: "12px", 
              fontWeight: "600", 
              padding: "2px 8px", 
              borderRadius: "4px" 
            }}>
              {totalRecords}
            </span>
          </div>
          <div>
            {isCollapsed ? <DownOutlined style={{ fontSize: "14px", color: "#595959" }} /> : <UpOutlined style={{ fontSize: "14px", color: "#595959" }} />}
          </div>
        </div>

        {!isCollapsed && (
          <div style={{ padding: "24px 24px 8px 24px" }}>
            <Spin spinning={loading}>
              {logs?.length === 0 ? (
                <div style={{ textAlign: "center", color: "#8c8c8c", padding: "20px 0" }}>No activity logs found.</div>
              ) : (
                logs?.map((log, index) => {
                  const isLast = index === logs.length - 1;
                  const actionConfig = getActionConfig(log.action);
                  const dateStr = dayjs(log.createdAt).format("DD MMM YYYY");
                  const timeStr = dayjs(log.createdAt).format("hh:mm A");

                  return (
                    <div key={log.logId || index} style={{ display: "flex", minHeight: "80px" }}>
                      
                      <div style={{ width: "90px", textAlign: "right", paddingRight: "20px", flexShrink: 0 }}>
                        <div style={{ fontSize: "12px", fontWeight: "500", color: "#262626" }}>{dateStr}</div>
                        <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "2px" }}>{timeStr}</div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "20px", flexShrink: 0 }}>
                        <div style={{ 
                          width: "16px", 
                          height: "16px", 
                          borderRadius: "50%", 
                          border: `3px solid ${actionConfig.textColor}`, 
                          background: "#ffffff", 
                          display: "flex", 
                          justifyContent: "center", 
                          alignItems: "center",
                          boxSizing: "border-box"
                        }}>
                          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: actionConfig.textColor }} />
                        </div>

                        {!isLast && (
                          <div style={{ 
                            width: "3px", 
                            background: actionConfig.textColor, 
                            flexGrow: 1, 
                            marginTop: "4px", 
                            marginBottom: "4px" 
                          }} />
                        )}
                      </div>

                      <div style={{ paddingLeft: "20px", paddingBottom: "24px", flexGrow: 1 }}>
                        <div 
                          style={{ fontSize: "13px", color: "#434343", lineHeight: "1.5" }}
                          dangerouslySetInnerHTML={{ __html: formatDesc(log) }}
                        />
                        
                        <div style={{ display: "flex", alignItems: "center", gap: "24px", marginTop: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Action:</span>
                            <span style={{ 
                              background: actionConfig.color, 
                              color: actionConfig.textColor, 
                              fontSize: "11px", 
                              fontWeight: "700", 
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              padding: "2px 8px", 
                              borderRadius: "4px" 
                            }}>
                              {actionConfig.label}
                            </span>
                          </div>
                        </div>

                        {log.payload && Object.keys(log.payload).length > 0 && (
                          <div style={{ 
                            marginTop: 10, 
                            padding: "10px 16px", 
                            background: "#fafafa", 
                            border: "1px solid #f0f0f0", 
                            borderRadius: "6px",
                            fontSize: "12px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.01)"
                          }}>
                            {Object.entries(log.payload)
                              .filter(([key]) => key !== "entityTypeId" && key !== "entityType" && key !== "businessTypeId" && key !== "businessType")
                              .map(([key, value]) => (
                                <div key={key} style={{ marginBottom: "6px" }}>
                                  <span style={{ fontWeight: "600", color: "#595959", textTransform: "capitalize" }}>{key.replace(/([A-Z])/g, ' $1').trim()}:</span>{" "}
                                  <span style={{ color: "#262626" }}>{formatPayloadValue(key, value)}</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>

                    </div>
                  );
                })
              )}
            </Spin>
            {totalRecords > limit && (
              <div style={{ textAlign: "right", marginTop: 10 }}>
                <Pagination
                  current={page}
                  total={totalRecords}
                  pageSize={limit}
                  onChange={(p) => setPage(p)}
                  size="small"
                />
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default EnterpriseActivityLog;
